import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box, Typography, Button, Stack, Chip, IconButton, TextField,
  Grid, Tooltip, CircularProgress, Divider, MenuItem, Select,
  FormControl, InputLabel, InputAdornment, alpha, Table,
  TableHead, TableBody, TableRow, TableCell, Skeleton, Collapse,
  Switch, FormControlLabel, Checkbox
} from '@mui/material';
import ConfirmDialog from '../components/ConfirmDialog';
import AddIcon            from '@mui/icons-material/Add';
import ArrowBackIcon      from '@mui/icons-material/ArrowBack';
import SaveIcon           from '@mui/icons-material/Save';
import DeleteIcon         from '@mui/icons-material/Delete';
import EditIcon           from '@mui/icons-material/Edit';
import LayersIcon         from '@mui/icons-material/Layers';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PercentIcon        from '@mui/icons-material/Percent';
import CurrencyRupeeIcon  from '@mui/icons-material/CurrencyRupee';
import CheckIcon          from '@mui/icons-material/Check';
import WarningAmberIcon   from '@mui/icons-material/WarningAmber';
import FunctionsIcon      from '@mui/icons-material/Functions';
import ExpandMoreIcon     from '@mui/icons-material/ExpandMore';
import ExpandLessIcon     from '@mui/icons-material/ExpandLess';
import RefreshIcon        from '@mui/icons-material/Refresh';
import CloseIcon          from '@mui/icons-material/Close';
import InfoOutlinedIcon   from '@mui/icons-material/InfoOutlined';
import LockOutlinedIcon   from '@mui/icons-material/LockOutlined';
import DragIndicatorIcon  from '@mui/icons-material/DragIndicator';
import { sidebarTooltips } from '../config/sidebarTooltips';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors, DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useSnackbar } from 'notistack';
import { useApp } from '../context/AppContext';
import {
  fetchSalaryTemplates, fetchSalaryTemplate,
  createSalaryTemplate, updateSalaryTemplate, deleteSalaryTemplate,
  toggleSalaryTemplateStatus,
  fetchElementSetupLookups,
  fetchRecords,
} from '../services/api';
import { PATTERNS } from '../config/fieldConfig';
import * as XLSX from 'xlsx';

// Limits for this form
const CTC_MIN = 1000;
const CTC_MAX = 999999999;          // ~99.99 crore
const COMPONENT_FLAT_MAX = 9999999; // ~99.99 lakh per component
const FORMULA_EXPR_MAX = 500;       // characters
// Looser pattern for Description — letters, digits, spaces, common punctuation only.
// Rejects @ # $ % ^ & * = + | \ etc.
const DESCRIPTION_RE = /^[A-Za-z0-9 .,'\-/()&]*$/;

// Number to Words Converter
const numberToWords = (num) => {
  if (num === 0) return 'Zero';
  const a = ['','One ','Two ','Three ','Four ', 'Five ','Six ','Seven ','Eight ','Nine ','Ten ','Eleven ','Twelve ','Thirteen ','Fourteen ','Fifteen ','Sixteen ','Seventeen ','Eighteen ','Nineteen '];
  const b = ['', '', 'Twenty','Thirty','Forty','Fifty', 'Sixty','Seventy','Eighty','Ninety'];
  const n = ('000000000' + num).substr(-9).match(/^(\d{2})(\d{2})(\d{2})(\d{1})(\d{2})$/);
  if (!n) return '';
  let str = '';
  str += (n[1] != 0) ? (a[Number(n[1])] || b[n[1][0]] + ' ' + a[n[1][1]]) + 'Crore ' : '';
  str += (n[2] != 0) ? (a[Number(n[2])] || b[n[2][0]] + ' ' + a[n[2][1]]) + 'Lakh ' : '';
  str += (n[3] != 0) ? (a[Number(n[3])] || b[n[3][0]] + ' ' + a[n[3][1]]) + 'Thousand ' : '';
  str += (n[4] != 0) ? (a[Number(n[4])] || b[n[4][0]] + ' ' + a[n[4][1]]) + 'Hundred ' : '';
  str += (n[5] != 0) ? ((str != '') ? 'and ' : '') + (a[Number(n[5])] || b[n[5][0]] + ' ' + a[n[5][1]]) : '';
  return str.trim() + ' Only';
};

// ── Design tokens ─────────────────────────────────────────────────────────────
const GREEN   = '#16a34a';
const GREEN_L = '#f0fdf4';
const GREEN_D = '#15803d';
const TEAL    = '#0891b2';
const AMBER   = '#d97706';
const BLUE    = '#2563eb';

const inputSx = {
  '& .MuiOutlinedInput-root': { borderRadius: '10px' },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: GREEN },
  '& label.Mui-focused': { color: GREEN },
};

// ── Formatters ─────────────────────────────────────────────────────────────────
const fmt = (n) => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(n || 0);

// ── Safe client-side formula evaluator ────────────────────────────────────────
function safeEval(expression, context = {}) {
  try {
    if (!expression || /[^a-zA-Z0-9_\s\+\-\*\/\(\)\.]/.test(expression)) return null;
    let expr = expression;
    // Use lowercased context for case-insensitive replacement
    const lowerContext = {};
    for (const k in context) {
      lowerContext[k.toLowerCase()] = context[k];
    }
    const keys = Object.keys(lowerContext).sort((a, b) => b.length - a.length);
    for (const key of keys) {
      const num = Number(lowerContext[key]);
      if (isNaN(num)) return null;
      expr = expr.replace(new RegExp('\\b' + key + '\\b', 'gi'), String(num));
    }
    // Remove allowed Math functions to check for unresolved variables safely
    const checkExpr = expr.replace(/Math\.(round|floor|ceil|max|min|abs)/g, '');
    if (/[a-zA-Z_]/.test(checkExpr)) return null; // unresolved variables
    // eslint-disable-next-line no-new-func
    return Function('"use strict"; return (' + expr + ');')();
  } catch { return null; }
}

// ── Detect variables in a formula expression ───────────────────────────────────
const RESERVED = new Set(['true','false','null','undefined','Infinity','NaN']);
function detectVars(expr) {
  if (!expr) return [];
  return [...new Set((expr.match(/\b[a-zA-Z_]\w*\b/g) || []).filter(v => !RESERVED.has(v)))];
}

const isBasicComponent = (c) => {
  if (!c) return false;
  const code = String(c.element_code || '').toUpperCase();
  const name = String(c.element_name || '').toUpperCase();
  return code === 'BASIC' || code === 'BS' || name.includes('BASIC');
};

/** Wraps a salary structure (component) row group for drag-and-drop reordering. */
function SortableRowShell({ id, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return children({
    setNodeRef,
    setActivatorNodeRef,
    style,
    attributes,
    listeners,
    isDragging,
  });
}

// ── Compute derived amounts ────────────────────────────────────────────────────
function computeAmounts(el, annualCtc, context = {}) {
  const v = Number(el.value) || 0;
  if (el.calculation_type === 'formula') {
    const formulaExpr = el.formula_expression;
    const result = formulaExpr ? safeEval(formulaExpr, context) : null;
    if (result !== null && !isNaN(result)) {
      return { monthly_amount: result, annual_amount: result * 12, formula_resolved: true };
    }
    return { monthly_amount: null, annual_amount: null, formula_resolved: false };
  }
  if (el.calculation_type === 'percentage_of_ctc') {
    const annual = (v / 100) * (Number(annualCtc) || 0);
    return { monthly_amount: annual / 12, annual_amount: annual, formula_resolved: null };
  }
  if (el.calculation_type === 'percentage_of_basic') {
    const basicMonthly = context['BASIC'] || context['basic'] || 0;
    const monthly = (v / 100) * basicMonthly;
    return { monthly_amount: monthly, annual_amount: monthly * 12, formula_resolved: null };
  }
  // flat
  return { monthly_amount: v, annual_amount: v * 12, formula_resolved: null };
}

// ═══════════════════════════════════════════════════════════════════════════════
// LIST VIEW
// ═══════════════════════════════════════════════════════════════════════════════
function TemplateList({ templates, loading, onAdd, onEdit, onDelete, onToggleStatus, onBulkDelete }) {
  const { enqueueSnackbar } = useSnackbar();
  const { state } = useApp();
  const activeCompanyId = state.activeContext?.company_id;
  const fileInputRef = useRef(null);
  
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleConfirmBulkDelete = async () => {
    setIsDeleting(true);
    const success = await onBulkDelete(selectedIds);
    if (success) {
      setSelectedIds([]);
      setBulkDeleteDialogOpen(false);
    }
    setIsDeleting(false);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(templates.map(t => t.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleExport = async () => {
    if (selectedIds.length === 0) {
      enqueueSnackbar('Please select at least one template to export', { variant: 'warning' });
      return;
    }
    enqueueSnackbar('Downloading file...', { variant: 'info' });
    let token = '';
    try {
      const auth = JSON.parse(localStorage.getItem('payroll.auth') || '{}');
      token = auth.token || '';
    } catch (e) {}

    try {
      const response = await fetch('/api/salary-templates/bulk-export', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ template_ids: selectedIds })
      });
      const result = await response.json();
      if (result.success) {
        if (result.data.length === 0) {
           enqueueSnackbar('No data to export', { variant: 'warning' });
           return;
        }
        const ws = XLSX.utils.json_to_sheet(result.data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Salary Templates");
        XLSX.writeFile(wb, `Salary_Templates_${new Date().toISOString().split('T')[0]}.xlsx`);
      } else {
        enqueueSnackbar(result.message || 'Export failed', { variant: 'error' });
      }
    } catch (err) {
      enqueueSnackbar('Failed to fetch export data: ' + err.message, { variant: 'error' });
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!activeCompanyId) {
      enqueueSnackbar('Please select an Active Company first before importing', { variant: 'warning' });
      return;
    }
    
    try {
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const records = XLSX.utils.sheet_to_json(ws);
      
      let token = '';
      try {
        const auth = JSON.parse(localStorage.getItem('payroll.auth') || '{}');
        token = auth.token || '';
      } catch (e) {}

      const bgId = state.activeContext?.bg_id || '';
      const lgId = state.activeContext?.ldg_id || '';
      const response = await fetch(`/api/salary-templates/bulk-import?active_company_id=${activeCompanyId}&active_bg_id=${bgId}&active_ldg_id=${lgId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ records })
      });
      const result = await response.json();
      if (result.success) {
        enqueueSnackbar(`Import complete: ${result.successCount} imported, ${result.skippedCount} skipped, ${result.errors?.length || 0} failed.`, { variant: 'success' });
        if (result.errors?.length > 0) {
           console.warn('Import errors:', result.errors);
           enqueueSnackbar('Check console for specific row errors', { variant: 'warning' });
        }
        window.location.reload(); 
      } else {
        enqueueSnackbar(result.message || 'Import failed', { variant: 'error' });
      }
    } catch (err) {
      enqueueSnackbar('Failed to process file: ' + err.message, { variant: 'error' });
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 2.5, p: 2, bgcolor: 'white', borderRadius: '14px', border: '1.5px solid #E5E7EB',
        boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" gap={2}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box sx={{ width: 40, height: 40, borderRadius: '12px',
              background: `linear-gradient(135deg, ${GREEN_D}, ${GREEN})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(22,163,74,0.3)' }}>
              <AccountBalanceWalletIcon sx={{ fontSize: 22, color: 'white' }} />
            </Box>
            <Box>
              <Typography fontWeight={700} fontSize={18} color="#111827" lineHeight={1.2}>Salary Templates</Typography>
              <Typography fontSize={12} color="text.secondary" mt={0.3}>
                {sidebarTooltips['__page:SALARY_TEMPLATE']}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" alignItems="center" gap={1}>
            {templates.length > 0 && (
              <FormControlLabel
                control={
                  <Checkbox 
                    size="small" 
                    checked={selectedIds.length === templates.length && templates.length > 0}
                    indeterminate={selectedIds.length > 0 && selectedIds.length < templates.length}
                    onChange={handleSelectAll} 
                  />
                }
                label={<Typography fontSize={13} fontWeight={600} color="text.secondary">Select All</Typography>}
                sx={{ mr: 1, ml: 1 }}
              />
            )}
            {selectedIds.length > 0 && (
              <Chip 
                label={`${selectedIds.length} selected`} 
                size="small" 
                sx={{ bgcolor: '#dbeafe', color: '#1e40af', fontWeight: 600, fontSize: 11, mr: 1 }}
              />
            )}
            {selectedIds.length > 0 && (
              <Button 
                size="small" 
                variant="outlined" 
                color="error"
                onClick={() => setBulkDeleteDialogOpen(true)}
                disabled={isDeleting}
                sx={{ borderColor: '#fecaca', textTransform: 'none', height: 36, '&:hover': { borderColor: '#ef4444', bgcolor: '#fef2f2' } }}
              >
                Delete
              </Button>
            )}
            <Button 
              size="small" 
              variant="outlined" 
              onClick={handleExport}
              sx={{ borderColor: '#E8EDF5', color: '#374151', textTransform: 'none', height: 36, '&:hover': { borderColor: '#16a34a', color: '#16a34a', bgcolor: '#f0fdf4' } }}
            >
              Export
            </Button>
            <Button 
              size="small" 
              variant="outlined" 
              component="label"
              sx={{ borderColor: '#E8EDF5', color: '#374151', textTransform: 'none', height: 36, '&:hover': { borderColor: '#2563eb', color: '#2563eb', bgcolor: '#eff6ff' } }}
            >
              Import
              <input type="file" accept=".xlsx, .xls" hidden ref={fileInputRef} onChange={handleImport} />
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={onAdd}
              sx={{
                px: 2.5, py: 1.1, borderRadius: '20px', fontWeight: 600, fontSize: 13.5,
                background: `linear-gradient(135deg, ${GREEN_D}, ${GREEN})`,
                boxShadow: '0 4px 16px rgba(22,163,74,0.35)',
                '&:hover': { background: `linear-gradient(135deg, #14532d, ${GREEN_D})` },
                whiteSpace: 'nowrap',
              }}>
              New Template
            </Button>
          </Stack>
        </Stack>
      </Box>

      {/* Template cards */}
      {loading ? (
        <Grid container spacing={2}>
          {[1,2,3].map(i => <Grid item xs={12} sm={6} md={4} key={i}><Skeleton height={160} sx={{ borderRadius: 2 }} /></Grid>)}
        </Grid>
      ) : templates.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 10 }}>
          <AccountBalanceWalletIcon sx={{ fontSize: 52, color: '#CBD5E1', mb: 1.5 }} />
          <Typography fontSize={16} fontWeight={600} color="text.secondary">No salary templates yet</Typography>
          <Typography fontSize={13} color="text.secondary" mb={2}>Create your first template to define salary structures</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={onAdd}
            sx={{ borderRadius: '20px', background: `linear-gradient(135deg, ${GREEN_D}, ${GREEN})` }}>
            Create Template
          </Button>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {templates.map(t => (
            <Grid item xs={12} sm={6} md={4} key={t.template_id}>
              <Box sx={{
                p: 2.5, bgcolor: 'white', borderRadius: '16px', border: '1.5px solid',
                borderColor: selectedIds.includes(t.id) ? GREEN : '#E5E7EB',
                boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                transition: 'all 0.15s',
                position: 'relative',
                '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.1)', borderColor: selectedIds.includes(t.id) ? GREEN : '#D1FAE5', transform: 'translateY(-1px)' },
              }}>
                <Box sx={{ position: 'absolute', top: 10, left: 10 }}>
                   <Checkbox 
                     size="small" 
                     checked={selectedIds.includes(t.id)} 
                     onChange={() => handleSelect(t.id)} 
                     sx={{ '& .MuiSvgIcon-root': { fontSize: 20 } }}
                   />
                </Box>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1.5} pl={3.5}>
                  <Box>
                    <Typography fontWeight={700} fontSize={14.5} color="#111827" lineHeight={1.3}>{t.template_name}</Typography>
                    {t.description && (
                      <Typography fontSize={12} color="text.secondary" mt={0.3} sx={{
                        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                      }}>{t.description}</Typography>
                    )}
                  </Box>
                  <Stack alignItems="flex-end">
                    <Chip label={t.status === 'ACTIVE' ? 'Active' : 'Inactive'} size="small"
                      sx={{ ml: 1, height: 20, fontSize: 10.5, flexShrink: 0,
                        bgcolor: t.status === 'ACTIVE' ? '#f0fdf4' : '#fef2f2',
                        color: t.status === 'ACTIVE' ? '#16a34a' : '#dc2626', fontWeight: 700 }} />
                    <FormControlLabel
                      control={<Switch size="small" checked={t.status === 'ACTIVE'} onChange={(e) => onToggleStatus(t, e.target.checked)} />}
                      label="Active"
                      labelPlacement="start"
                      sx={{ mt: 0.5, mr: 0, ml: 1, '& .MuiFormControlLabel-label': { fontSize: 11, fontWeight: 600, color: 'text.secondary' } }}
                    />
                  </Stack>
                </Stack>

                <Box sx={{ p: 1.5, borderRadius: '10px', bgcolor: GREEN_L, mb: 1.5 }}>
                  <Typography fontSize={11} color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>Annual CTC</Typography>
                  <Typography fontSize={20} fontWeight={800} color={GREEN_D} lineHeight={1.2}>
                    ₹{fmt(t.annual_ctc)}
                  </Typography>
                  <Typography fontSize={11} color={GREEN} fontWeight={500}>₹{fmt(t.annual_ctc / 12)} / month</Typography>
                </Box>

                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Chip label={`${t.element_count || 0} components`} size="small"
                    sx={{ height: 20, fontSize: 11, bgcolor: '#F1F5F9', color: '#475569', fontWeight: 600 }} />
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="Edit template">
                      <IconButton size="small" onClick={() => onEdit(t)}
                        sx={{ color: GREEN, '&:hover': { bgcolor: GREEN_L } }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete template">
                      <IconButton size="small" onClick={() => onDelete(t)}
                        sx={{ color: '#ef4444', '&:hover': { bgcolor: '#fef2f2' } }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Bulk Delete Confirmation Dialog */}
      <ConfirmDialog
        open={bulkDeleteDialogOpen}
        title="Confirm Delete"
        message="Are you sure you want to delete the selected Salary Templates? This action cannot be undone."
        confirmLabel={isDeleting ? "Deleting..." : "Delete"}
        onConfirm={handleConfirmBulkDelete}
        onCancel={() => setBulkDeleteDialogOpen(false)}
        confirmColor="error"
      />
    </Box>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TEMPLATE EDITOR
// ═══════════════════════════════════════════════════════════════════════════════
function TemplateEditor({ templateId, onBack, onSaved }) {
  const isEdit = !!templateId;
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { state } = useApp();
  const activeContextKey = [
    state.activeContext?.company_id || '',
    state.activeContext?.bg_id || '',
    state.activeContext?.ldg_id || '',
    state.activeContext?.payroll_id || '',
  ].join('|');
  const basicToastIdRef = useRef(null);

  const [loading, setSaving]     = useState(false);
  const [saving, setSavingState] = useState(false);

  // Form state
  const [info, setInfo] = useState({
    template_name: '', description: '',
    company_id: '', bg_id: '', lg_id: '',
    annual_ctc: '', active_flag: 'Y', status: 'ACTIVE',
    grade: '',
  });
  const [components, setComponents] = useState([]);
  const [errors, setErrors]         = useState({});
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [basicAlertDismissed, setBasicAlertDismissed] = useState(false);
  const [activeDragId, setActiveDragId] = useState(null);

  const [formulaDrafts, setFormulaDrafts] = useState({});
  const [confirmFormulaState, setConfirmFormulaState] = useState({ open: false, index: -1, c: null, draftValue: '' });

  const handleUpdateFormulaClick = (c, index, draftValue) => {
    setConfirmFormulaState({ open: true, index, c, draftValue });
  };

  const confirmUpdateFormula = () => {
    const { index, c, draftValue } = confirmFormulaState;
    if (index >= 0 && c) {
      setComp(index, 'formula_expression', draftValue);
      if (!c.is_formula_override) setComp(index, 'is_formula_override', true);
      setFormulaDrafts(prev => {
        const next = { ...prev };
        delete next[c.element_id];
        return next;
      });
      enqueueSnackbar('Formula updated successfully', { variant: 'success' });
    }
    setConfirmFormulaState({ open: false, index: -1, c: null, draftValue: '' });
  };

  const componentSortIds = useMemo(
    () => components.map(c => c.element_id),
    [components]
  );

  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Lookup data (elements comes from lookups, not separate fetch)
  const [allElements, setAllElements] = useState([]);
  const [lookups, setLookups]         = useState({ companies: [], businessGroups: [], legislations: [] });
  const [gradeOptions, setGradeOptions] = useState([]);

  const prevCompanyForComponents = useRef(null);

  // Load lookups + existing template
  useEffect(() => {
    prevCompanyForComponents.current = null;
    setBasicAlertDismissed(false);
    setSaving(true);
    Promise.all([
      fetchElementSetupLookups(),
      fetchRecords('GRADE_MASTER'),
      isEdit ? fetchSalaryTemplate(templateId) : Promise.resolve(null),
    ]).then(([lkRes, gradeRes, tmplRes]) => {
      const grades = (gradeRes.data?.data || [])
        .filter(g => g.active_flag !== 'N')
        .sort((a, b) => (a.grade_level ?? 0) - (b.grade_level ?? 0))
        .map(g => ({
          value: g.grade_code,
          label: [g.grade_code, g.grade_name].filter(Boolean).join(' - '),
        }));
      setGradeOptions(grades);
      const lk = lkRes.data?.data || {};
      setLookups(lk);
      setAllElements(lk.elements || []);
      if (tmplRes) {
        const { template: t, elements } = tmplRes.data.data;
        setInfo({
          template_name: t.template_name || '',
          description:   t.description   || '',
          company_id:    t.company_id    || '',
          bg_id:         t.bg_id         || '',
          lg_id:         t.lg_id         || '',
          annual_ctc:    t.annual_ctc    || '',
          active_flag:   t.active_flag   || 'Y',
          status:        t.status        || 'ACTIVE',
          grade:         t.grade         || '',
        });
        setComponents(elements.map((el, i) => ({
          element_id:               el.element_id,
          element_name:             el.element_name,
          element_code:             el.element_code,
          element_type:             el.element_type || el.type,
          calculation_rule:         el.calculation_rule  || null,
          calculation_type:         el.calculation_type,
          value:                    el.value,
          base_element:             el.base_element || null,
          formula_expression:       el.formula_expression       || null,
          is_formula_override:      el.is_formula_override      || false,
          default_formula_expression: el.default_formula_expression || null,
          sequence:                 el.sequence ?? i + 1,
        })));
      }
    }).catch(() => enqueueSnackbar('Failed to load data', { variant: 'error' }))
      .finally(() => setSaving(false));
  }, [templateId, activeContextKey]);

  // 3-pass enriched computation so dependent elements resolve correctly:
  //   Pass 1: flat   → BASIC monthly value enters context
  //   Pass 2: % (CTC / Basic) → uses context; result also enters context
  //   Pass 3: formula → uses full context
  const enriched = useMemo(() => {
    const annualCtc = Number(info.annual_ctc) || 0;
    const ctx = {
      ctc_annual: annualCtc,
      ctc_monthly: annualCtc / 12,
    };
    const addCtx = (c, monthly) => {
      if (monthly !== null) {
        if (c.element_code) {
          ctx[c.element_code]             = monthly;
          ctx[c.element_code.toLowerCase()] = monthly;
        }
        if (isBasicComponent(c)) {
          ctx['BASIC'] = monthly;
          ctx['basic'] = monthly;
        }
      }
    };

    const IS_PCT = t => t === 'percentage_of_ctc' || t === 'percentage_of_basic';

    // Pass 1: flat
    const p1 = components.map(c => {
      if (c.calculation_type !== 'flat') return { ...c, monthly_amount: null, annual_amount: null, formula_resolved: null };
      const { monthly_amount, annual_amount } = computeAmounts(c, annualCtc, ctx);
      addCtx(c, monthly_amount);
      return { ...c, monthly_amount, annual_amount, formula_resolved: null };
    });

    // Pass 2a: % of CTC (e.g. Basic) — always before % of Basic regardless of row order
    const p2a = p1.map(c => {
      if (c.calculation_type !== 'percentage_of_ctc') return c;
      const { monthly_amount, annual_amount } = computeAmounts(c, annualCtc, ctx);
      addCtx(c, monthly_amount);
      return { ...c, monthly_amount, annual_amount, formula_resolved: null };
    });

    // Pass 2b: % of Basic (e.g. HRA) — uses BASIC from pass 2a
    const p2b = p2a.map(c => {
      if (c.calculation_type !== 'percentage_of_basic') return c;
      const { monthly_amount, annual_amount } = computeAmounts(c, annualCtc, ctx);
      addCtx(c, monthly_amount);
      return { ...c, monthly_amount, annual_amount, formula_resolved: null };
    });

    // Pass 3: formula (multiple iterations for dependencies)
    let currentComponents = p2b.map(c => ({ ...c }));
    let formulas = currentComponents.filter(c => c.calculation_type === 'formula');
    let maxIter = formulas.length || 1;
    let resolved = 0;
    
    for (let iter = 0; iter < maxIter; iter++) {
      let progress = false;
      for (let c of formulas) {
        if (!c.formula_resolved) {
          const { monthly_amount, annual_amount, formula_resolved } = computeAmounts(c, annualCtc, ctx);
          if (formula_resolved) {
            c.monthly_amount = monthly_amount;
            c.annual_amount = annual_amount;
            c.formula_resolved = formula_resolved;
            addCtx(c, monthly_amount);
            progress = true;
            resolved++;
          }
        }
      }
      if (!progress || resolved === formulas.length) break;
    }
    
    return currentComponents;
  }, [components, info.annual_ctc]);

  // Totals: only earning rows (deductions don't add to CTC)
  const totalMonthly = enriched.reduce((s, c) => s + (c.element_type === 'earning' && c.monthly_amount !== null ? c.monthly_amount : 0), 0);
  const totalAnnual  = enriched.reduce((s, c) => s + (c.element_type === 'earning' && c.annual_amount  !== null ? c.annual_amount  : 0), 0);
  const annualCtcNum = Number(info.annual_ctc) || 0;
  const monthlyCtcNum = annualCtcNum / 12;
  const difference   = monthlyCtcNum - totalMonthly; // MONTHLY difference
  const pctUsed      = annualCtcNum > 0 ? (totalAnnual / annualCtcNum) * 100 : 0;

  const hasBasicSalary = useMemo(
    () => components.length === 0 || components.some(isBasicComponent),
    [components]
  );

  const shouldShowBasicToast =
    components.length > 0 && !hasBasicSalary && !basicAlertDismissed;

  const resetSalaryComponents = useCallback(() => {
    setComponents([]);
    setExpandedRows(new Set());
    setActiveDragId(null);
    setBasicAlertDismissed(false);
    if (basicToastIdRef.current != null) {
      closeSnackbar(basicToastIdRef.current);
      basicToastIdRef.current = null;
    }
    setErrors(prev => {
      if (!prev.components) return prev;
      const next = { ...prev };
      delete next.components;
      return next;
    });
  }, [closeSnackbar]);

  useEffect(() => {
    if (!shouldShowBasicToast) {
      if (basicToastIdRef.current != null) {
        closeSnackbar(basicToastIdRef.current);
        basicToastIdRef.current = null;
      }
      return;
    }
    if (basicToastIdRef.current != null) return;

    const id = enqueueSnackbar('Please add a Basic Salary Structure (Component).', {
      variant: 'warning',
      persist: true,
      preventDuplicate: true,
      anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
      action: (snackbarId) => (
        <IconButton
          size="small"
          aria-label="Close"
          color="inherit"
          onClick={() => {
            setBasicAlertDismissed(true);
            closeSnackbar(snackbarId);
            basicToastIdRef.current = null;
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      ),
    });
    basicToastIdRef.current = id;
  }, [shouldShowBasicToast, enqueueSnackbar, closeSnackbar]);

  useEffect(() => () => {
    if (basicToastIdRef.current != null) {
      closeSnackbar(basicToastIdRef.current);
      basicToastIdRef.current = null;
    }
  }, [closeSnackbar]);

  // Left panel — only elements belonging to the selected company
  const usedIds = new Set(components.map(c => c.element_id));
  const companyElements = useMemo(() => {
    if (!info.company_id) return [];
    return allElements.filter(e => e.company_id === info.company_id);
  }, [allElements, info.company_id]);

  const earnings   = companyElements.filter(e => e.type === 'earning'   && e.active_flag === 'Y');
  const deductions = companyElements.filter(e => e.type === 'deduction' && e.active_flag === 'Y');

  // Drop salary structure (components) that no longer belong when company changes (not on element refresh)
  useEffect(() => {
    if (!info.company_id || allElements.length === 0) return;
    if (prevCompanyForComponents.current === info.company_id) return;
    prevCompanyForComponents.current = info.company_id;

    const validIds = new Set(
      allElements.filter(e => e.company_id === info.company_id).map(e => e.element_id)
    );
    setComponents(prev => {
      const next = prev.filter(c => validIds.has(c.element_id));
      if (next.length === prev.length) return prev;
      return next.map((c, i) => ({ ...c, sequence: i + 1 }));
    });
  }, [info.company_id, allElements]);

  const addComponent = (el) => {
    if (usedIds.has(el.element_id)) return;
    const rule      = el.calculation_rule || 'flat';
    const isFlat    = rule === 'flat';
    const isPct     = rule === 'percentage';
    const isFormula = rule === 'formula';

    // Determine calculation_type from rule + base_element
    let calcType = 'flat';
    if (isPct) {
      const base = (el.base_element || '').toUpperCase();
      calcType = (base && base !== 'CTC') ? 'percentage_of_basic' : 'percentage_of_ctc';
    } else if (isFormula) {
      calcType = 'formula';
    }

    setComponents(p => [...p, {
      element_id:               el.element_id,
      element_name:             el.element_name,
      element_code:             el.element_code,
      element_type:             el.type || el.element_type,
      calculation_rule:         rule,
      calculation_type:         calcType,
      // Auto-fill value from element definition
      value:                    isPct ? (el.percentage_value ?? '') : isFlat ? (el.flat_amount || '') : 0,
      base_element:             isPct ? (el.base_element || 'CTC') : null,
      formula_expression:       isFormula ? (el.default_formula_expression || '') : null,
      is_formula_override:      false,
      default_formula_expression: el.default_formula_expression || null,
      sequence:                 p.length + 1,
    }]);
    if (errors.components) setErrors(p => { const n = {...p}; delete n.components; return n; });
  };

  const removeComponent = (idx) => {
    const removedId = components[idx]?.element_id;
    setComponents(p => p.filter((_, i) => i !== idx).map((c, i) => ({ ...c, sequence: i + 1 })));
    if (removedId) {
      setExpandedRows(prev => {
        const next = new Set(prev);
        next.delete(removedId);
        return next;
      });
    }
  };

  const handleComponentDragEnd = (event) => {
    const { active, over } = event;
    setActiveDragId(null);
    if (!over || active.id === over.id) return;
    setComponents(items => {
      const oldIndex = items.findIndex(c => c.element_id === active.id);
      const newIndex = items.findIndex(c => c.element_id === over.id);
      if (oldIndex < 0 || newIndex < 0) return items;
      return arrayMove(items, oldIndex, newIndex).map((c, i) => ({ ...c, sequence: i + 1 }));
    });
  };

  const draggedComponent = activeDragId
    ? enriched.find(c => c.element_id === activeDragId)
    : null;

  const setComp = (idx, field, val) =>
    setComponents(p => p.map((c, i) => i === idx ? { ...c, [field]: val } : c));

  const [refreshingElements, setRefreshingElements] = useState(false);
  const refreshElements = () => {
    setRefreshingElements(true);
    fetchElementSetupLookups()
      .then(lkRes => {
        const lk = lkRes.data?.data || {};
        setLookups(lk);
        setAllElements(lk.elements || []);
      })
      .catch(() => {})
      .finally(() => setRefreshingElements(false));
  };

  const toggleExpand = (elementId) => setExpandedRows(prev => {
    const next = new Set(prev);
    next.has(elementId) ? next.delete(elementId) : next.add(elementId);
    return next;
  });

  const handleAnnualCtcChange = (raw) => {
    const digits = raw.replace(/[^0-9]/g, '');
    if (!digits) {
      setI('annual_ctc', '');
      return;
    }
    const num = Number(digits);
    if (num > CTC_MAX) {
      setI('annual_ctc', String(CTC_MAX));
      setErrors(p => ({
        ...p,
        annual_ctc: `Annual CTC cannot exceed ₹${CTC_MAX.toLocaleString('en-IN')}`,
      }));
      return;
    }
    setI('annual_ctc', digits);
    if (errors.annual_ctc) setErrors(p => { const n = { ...p }; delete n.annual_ctc; return n; });
  };

  const setI = (field, val) => {
    if (field === 'bg_id' && val !== info.bg_id) {
      resetSalaryComponents();
    }
    if (field === 'company_id' && val !== info.company_id) {
      resetSalaryComponents();
    }

    setInfo(p => {
      const next = { ...p, [field]: val };
      // Cascade: changing Business Group clears Company and Legislation
      if (field === 'bg_id') {
        if (p.company_id) next.company_id = '';
        next.lg_id = '';
        prevCompanyForComponents.current = null;
      }
      // Legislation follows company's default legislation (read-only in UI)
      if (field === 'company_id') {
        const company = (lookups.companies || []).find(c => c.id === val);
        next.lg_id = company?.default_ldg_id || '';
      }
      return next;
    });
    if (errors[field]) setErrors(p => { const n = {...p}; delete n[field]; return n; });
    if (field === 'company_id' && errors.lg_id) {
      setErrors(p => { const n = { ...p }; delete n.lg_id; return n; });
    }
  };

  const legislationLabel = useMemo(() => {
    if (!info.lg_id) return '';
    const leg = (lookups.legislations || []).find(l => l.id === info.lg_id);
    return leg?.label || info.lg_id;
  }, [info.lg_id, lookups.legislations]);

  // Back-fill legislation when company is set but lg_id is missing (e.g. after lookups load)
  useEffect(() => {
    if (!info.company_id || info.lg_id) return;
    const company = (lookups.companies || []).find(c => c.id === info.company_id);
    if (company?.default_ldg_id) {
      setInfo(p => ({ ...p, lg_id: company.default_ldg_id }));
    }
  }, [info.company_id, info.lg_id, lookups.companies]);

  // Validation
  const validate = () => {
    const e = {};
    // #17 Template Name — required + PATTERNS.NAME (letters/spaces/hyphens/apostrophes/dots)
    if (!info.template_name.trim()) {
      e.template_name = 'Template name is required';
    } else if (!new RegExp(PATTERNS.NAME.pattern).test(info.template_name.trim())) {
      e.template_name = PATTERNS.NAME.patternMsg;
    }
    // #18 Description — optional, but if provided, reject special chars (allow common punctuation)
    if (info.description && !DESCRIPTION_RE.test(info.description)) {
      e.description = 'Letters, digits, spaces and common punctuation (. , - / ( ) & \' ) only';
    }
    if (!info.bg_id)                e.bg_id          = 'Business Group is required';
    if (!info.company_id)           e.company_id     = 'Company is required';
    else if (!info.lg_id)           e.lg_id          = 'Company has no default legislation — set it on the Company record';
    // #19 Annual CTC — required + range
    if (!annualCtcNum) {
      e.annual_ctc = 'Annual CTC is required';
    } else if (!Number.isFinite(annualCtcNum)) {
      e.annual_ctc = 'Annual CTC must be a valid number';
    } else if (annualCtcNum < CTC_MIN) {
      e.annual_ctc = `Annual CTC must be at least ₹${CTC_MIN.toLocaleString('en-IN')}`;
    } else if (annualCtcNum > CTC_MAX) {
      e.annual_ctc = `Annual CTC cannot exceed ₹${CTC_MAX.toLocaleString('en-IN')}`;
    }
    if (components.length === 0)    e.components     = 'Add at least one salary structure (component)';
    const missingVal = components.some(c => c.calculation_type !== 'formula' && !c.value && c.value !== 0);
    const missingFrm = components.some(c => c.calculation_type === 'formula' && !c.formula_expression?.trim());
    if (missingVal) e.components = 'All non-formula components need a value';
    if (missingFrm) e.components = 'All formula components need a formula expression';
    // #20 Per-component limits
    const overLimit = [];
    components.forEach((c, idx) => {
      if (c.calculation_type === 'formula') {
        const expr = String(c.formula_expression || '');
        if (expr.length > FORMULA_EXPR_MAX) {
          overLimit.push(`row ${idx + 1}: formula exceeds ${FORMULA_EXPR_MAX} chars`);
        }
      } else if (c.calculation_type === 'flat') {
        const n = Number(c.value);
        if (c.value !== '' && c.value !== null && c.value !== undefined && Number.isFinite(n)) {
          if (n < 0)                   overLimit.push(`row ${idx + 1}: flat amount must be ≥ 0`);
          else if (n > COMPONENT_FLAT_MAX) overLimit.push(`row ${idx + 1}: flat amount exceeds ₹${COMPONENT_FLAT_MAX.toLocaleString('en-IN')}`);
        }
      } else if (c.calculation_type === 'percentage_of_ctc' || c.calculation_type === 'percentage_of_basic') {
        const n = Number(c.value);
        if (c.value !== '' && c.value !== null && c.value !== undefined && Number.isFinite(n)) {
          if (n < 0 || n > 100) overLimit.push(`row ${idx + 1}: percentage must be 0-100`);
        }
      }
    });
    if (overLimit.length) {
      e.components = (e.components ? e.components + ' • ' : '') + overLimit.join('; ');
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // Save
  const handleSave = async () => {
    if (!validate()) return;
    setSavingState(true);
    try {
      const payload = {
        template: { ...info, annual_ctc: annualCtcNum, status: info.status || 'ACTIVE' },
        elements: enriched.map((c, i) => ({
          element_id:          c.element_id,
          calculation_type:    c.calculation_type,
          value:               Number(c.value) || 0,
          formula_expression:  c.formula_expression  || null,
          is_formula_override: c.is_formula_override || false,
          monthly_amount:      Math.round(c.monthly_amount ?? 0),
          annual_amount:       Math.round(c.annual_amount  ?? 0),
          sequence:            i + 1,
        })),
      };
      if (isEdit) {
        await updateSalaryTemplate(templateId, payload);
        enqueueSnackbar('Template updated', { variant: 'success' });
      } else {
        await createSalaryTemplate(payload);
        enqueueSnackbar('Template created', { variant: 'success' });
      }
      onSaved();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Save failed', { variant: 'error' });
    } finally {
      setSavingState(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton height={60} sx={{ mb: 2, borderRadius: 2 }} />
        <Skeleton height={120} sx={{ mb: 2, borderRadius: 2 }} />
        <Skeleton height={300} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* ── Top bar ── */}
      <Box sx={{ px: 3, py: 1.5, bgcolor: 'white', borderBottom: '1.5px solid #E5E7EB',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <IconButton size="small" onClick={onBack}
            sx={{ color: '#64748B', border: '1.5px solid #E2E8F0', borderRadius: '8px',
              '&:hover': { bgcolor: GREEN_L, borderColor: GREEN, color: GREEN } }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Box>
            <Typography fontWeight={700} fontSize={16} color="#111827" lineHeight={1.2}>
              {isEdit ? 'Edit Salary Template' : 'New Salary Template'}
            </Typography>
            <Typography fontSize={11.5} color="text.secondary">
              {info.template_name || 'Untitled Template'}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={onBack}
            sx={{ borderRadius: '10px', fontWeight: 600, fontSize: 13,
              borderColor: '#E2E8F0', color: '#64748B',
              '&:hover': { borderColor: '#CBD5E1', bgcolor: '#F8FAFC' } }}>
            Cancel
          </Button>
          <Button variant="contained"
            startIcon={saving ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <SaveIcon />}
            onClick={handleSave} disabled={saving}
            sx={{ borderRadius: '10px', fontWeight: 700, fontSize: 13,
              background: `linear-gradient(135deg, ${GREEN_D}, ${GREEN})`,
              boxShadow: `0 4px 14px ${alpha(GREEN, 0.4)}` }}>
            {saving ? 'Saving…' : (isEdit ? 'Update Template' : 'Save Template')}
          </Button>
        </Stack>
      </Box>

      {/* ── Content ── */}
      <Box sx={{ flex: 1, overflow: 'auto', bgcolor: '#F8FAFC' }}>

        {/* Info section */}
        <Box sx={{ mx: 3, mt: 2.5, mb: 2, p: 2.5, bgcolor: 'white', borderRadius: '14px', border: '1.5px solid #E8EDF5' }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={5}>
              <TextField size="small" fullWidth label="Template Name *"
                value={info.template_name}
                onChange={e => setI('template_name', e.target.value)}
                error={!!errors.template_name} helperText={errors.template_name}
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField size="small" fullWidth label="Description"
                value={info.description}
                onChange={e => setI('description', e.target.value)}
                placeholder="e.g. Standard salary structure for field employees"
                error={!!errors.description}
                helperText={errors.description || ''}
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <FormControl size="small" fullWidth sx={inputSx}>
                <InputLabel>Grade</InputLabel>
                <Select value={info.grade || ''} label="Grade" onChange={e => setI('grade', e.target.value)}>
                  <MenuItem value=""><em>None</em></MenuItem>
                  {info.grade && !gradeOptions.some(g => g.value === info.grade) && (
                    <MenuItem value={info.grade}>{info.grade}</MenuItem>
                  )}
                  {gradeOptions.map(g => (
                    <MenuItem key={g.value} value={g.value}>{g.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl size="small" fullWidth error={!!errors.bg_id} sx={inputSx}>
                <InputLabel>Business Group *</InputLabel>
                <Select value={info.bg_id} label="Business Group *" onChange={e => setI('bg_id', e.target.value)}>
                  {(lookups.businessGroups || []).map(b => (
                    <MenuItem key={b.id} value={b.id}>{b.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              {(() => {
                const selectedBg = (lookups.businessGroups || []).find(b => b.id === info.bg_id);
                const allCompanies = lookups.companies || [];
                let filteredCompanies = info.bg_id
                  ? allCompanies.filter(c => c.id === selectedBg?.company_id)
                  : [];
                // Tolerant fallback: keep an existing Company visible even if it doesn't match the BG's company link
                if (info.company_id && !filteredCompanies.find(c => c.id === info.company_id)) {
                  const legacy = allCompanies.find(c => c.id === info.company_id);
                  if (legacy) filteredCompanies = [...filteredCompanies, legacy];
                }
                return (
                  <FormControl size="small" fullWidth error={!!errors.company_id} disabled={!info.bg_id} sx={inputSx}>
                    <InputLabel>Company *</InputLabel>
                    <Select
                      value={info.company_id}
                      label="Company *"
                      disabled={!info.bg_id}
                      onChange={e => setI('company_id', e.target.value)}
                      error={!!errors.company_id}
                    >
                      {filteredCompanies.map(c => <MenuItem key={c.id} value={c.id}>{c.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                );
              })()}
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                size="small"
                fullWidth
                label="Legislation"
                value={legislationLabel}
                placeholder={info.company_id ? 'No default legislation on company' : 'Select a company first'}
                error={!!errors.lg_id}
                helperText={errors.lg_id || (info.company_id ? 'From company default — not editable' : '')}
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlinedIcon sx={{ fontSize: 16, color: '#94A3B8' }} />
                    </InputAdornment>
                  ),
                }}
                InputLabelProps={{ shrink: true }}
                disabled={!info.company_id}
                sx={{
                  ...inputSx,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    bgcolor: info.company_id ? '#F8FAFC' : undefined,
                  },
                }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* CTC input */}
        <Box sx={{ mx: 3, mb: 2, p: 2.5, bgcolor: 'white', borderRadius: '14px', border: '1.5px solid #E8EDF5' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
            <Box>
              <Typography fontSize={13} fontWeight={700} color="#374151" mb={0.5}>Annual CTC</Typography>
              <Typography fontSize={12} color="text.secondary">Total Cost to Company per year — base for % calculations</Typography>
            </Box>
            <Stack direction="row" alignItems="center" spacing={2}>
              {annualCtcNum > 0 && (
                <Typography fontSize={12} fontWeight={600} color={GREEN_D} sx={{ fontStyle: 'italic', textAlign: 'right', maxWidth: 250, lineHeight: 1.2 }}>
                  Rupees {numberToWords(annualCtcNum)}
                </Typography>
              )}
              <TextField size="small"
                value={info.annual_ctc}
                onChange={e => handleAnnualCtcChange(e.target.value)}
                error={!!errors.annual_ctc}
                helperText={
                  errors.annual_ctc
                    || `Min ₹${CTC_MIN.toLocaleString('en-IN')} · Max ₹${CTC_MAX.toLocaleString('en-IN')}`
                }
                placeholder="e.g. 600000"
                inputProps={{ maxLength: String(CTC_MAX).length }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><CurrencyRupeeIcon sx={{ fontSize: 16, color: GREEN }} /></InputAdornment>,
                  sx: { fontFamily: 'monospace', fontWeight: 700, fontSize: 15, bgcolor: GREEN_L, borderRadius: '10px' },
                }}
                sx={{ ...inputSx, width: 260 }}
              />
              {annualCtcNum > 0 && (
                <Box>
                  <Typography fontSize={11} color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.4}>Monthly</Typography>
                  <Typography fontSize={16} fontWeight={800} color={GREEN_D} fontFamily="monospace">₹{fmt(annualCtcNum / 12)}</Typography>
                </Box>
              )}
            </Stack>
          </Stack>
          {annualCtcNum > 0 && components.length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.5}>
                <Typography fontSize={11} color="text.secondary">Allocated: {pctUsed.toFixed(1)}%</Typography>
                <Typography fontSize={11} color={Math.abs(difference) < 1 ? GREEN : '#d97706'} fontWeight={600}>
                  {Math.abs(difference) < 1 ? '✓ Fully allocated' : difference > 0 ? `₹${fmt(difference)} unallocated/mo` : `₹${fmt(-difference)} over CTC/mo`}
                </Typography>
              </Stack>
              <Box sx={{ height: 6, bgcolor: '#E5E7EB', borderRadius: 3, overflow: 'hidden' }}>
                <Box sx={{
                  height: '100%', borderRadius: 3,
                  width: `${Math.min(pctUsed, 100)}%`,
                  bgcolor: pctUsed > 100 ? '#ef4444' : pctUsed > 90 ? GREEN : TEAL,
                  transition: 'width 0.3s ease',
                }} />
              </Box>
            </Box>
          )}
        </Box>

        {/* Main two-column section */}
        <Box sx={{ mx: 3, mb: 3, display: 'flex', gap: 2, alignItems: 'flex-start' }}>

          {/* ── Left panel: available components ── */}
          <Box sx={{
            width: 230, flexShrink: 0,
            bgcolor: 'white', borderRadius: '14px', border: '1.5px solid #E8EDF5',
            position: 'sticky', top: 16,
            overflow: 'hidden',
          }}>
            <Box sx={{ px: 2, py: 1.5, bgcolor: '#F8FAFC', borderBottom: '1.5px solid #E8EDF5' }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography fontSize={12} fontWeight={700} color="text.secondary" textTransform="uppercase" letterSpacing={0.6}>
                  Available Components
                </Typography>
                <Tooltip title="Refresh list">
                  <IconButton size="small" onClick={refreshElements} disabled={refreshingElements}
                    sx={{ color: '#9CA3AF', '&:hover': { color: GREEN, bgcolor: GREEN_L } }}>
                    <RefreshIcon sx={{ fontSize: 15, ...(refreshingElements ? { animation: 'spin 1s linear infinite' } : {}) }} />
                  </IconButton>
                </Tooltip>
              </Stack>
              <Typography fontSize={11} color="text.secondary">
                {info.company_id
                  ? 'Showing elements for selected company · click to add'
                  : 'Select a company to load available components'}
              </Typography>
            </Box>
            <Box sx={{ maxHeight: 480, overflowY: 'auto', pb: 1,
              '&::-webkit-scrollbar': { width: 3 },
              '&::-webkit-scrollbar-thumb': { bgcolor: '#E5E7EB', borderRadius: 2 } }}>
              {!info.company_id ? (
                <Typography fontSize={12} color="text.secondary" sx={{ px: 2, py: 3, textAlign: 'center' }}>
                  Choose Company * above to see earnings and deductions for that company.
                </Typography>
              ) : earnings.length === 0 && deductions.length === 0 ? (
                <Typography fontSize={12} color="text.secondary" sx={{ px: 2, py: 3, textAlign: 'center' }}>
                  No active elements for this company. Add them in Element Setup, then refresh.
                </Typography>
              ) : [
                { label: 'Earnings', items: earnings, color: '#16a34a', bg: '#f0fdf4' },
                { label: 'Deductions', items: deductions, color: '#dc2626', bg: '#fef2f2' },
              ].map(group => group.items.length > 0 && (
                <Box key={group.label}>
                  <Typography fontSize={10.5} fontWeight={700} color="text.secondary"
                    textTransform="uppercase" letterSpacing={0.5}
                    sx={{ px: 2, pt: 1.5, pb: 0.5 }}>{group.label}</Typography>
                  {group.items.map(el => {
                    const added     = usedIds.has(el.element_id);
                    const isFormula = el.calculation_rule === 'formula';
                    return (
                      <Box key={el.element_id}
                        onClick={() => !added && addComponent(el)}
                        sx={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          px: 2, py: 0.9,
                          cursor: added ? 'default' : 'pointer',
                          opacity: added ? 0.55 : 1,
                          transition: 'all 0.12s',
                          '&:hover': added ? {} : { bgcolor: group.bg },
                        }}>
                        <Box>
                          <Typography fontSize={13} fontWeight={500} color="#374151">{el.element_name}</Typography>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Typography fontSize={10.5} color="text.secondary" fontFamily="monospace">{el.element_code}</Typography>
                            {isFormula && <Chip label="f(x)" size="small" sx={{ height: 14, fontSize: 9, px: 0.3,
                              bgcolor: '#eff6ff', color: BLUE, fontWeight: 700 }} />}
                          </Stack>
                        </Box>
                        {added
                          ? <CheckIcon sx={{ fontSize: 14, color: group.color }} />
                          : <AddIcon  sx={{ fontSize: 14, color: '#9CA3AF' }} />}
                      </Box>
                    );
                  })}
                </Box>
              ))}
            </Box>
          </Box>

          {/* ── Right: components table ── */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ bgcolor: 'white', borderRadius: '14px', border: '1.5px solid #E8EDF5', overflow: 'hidden' }}>
              {/* Table header */}
              <Box sx={{ px: 2.5, py: 1.5, bgcolor: '#F8FAFC', borderBottom: '1.5px solid #E8EDF5',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography fontSize={12} fontWeight={700} color="text.secondary" textTransform="uppercase" letterSpacing={0.6}>
                  Salary Structure
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  {components.some(c => c.calculation_type === 'formula') && (
                    <Chip label="Contains formula components" size="small" icon={<FunctionsIcon sx={{ fontSize: 11 }} />}
                      sx={{ height: 20, fontSize: 10.5, bgcolor: '#eff6ff', color: BLUE, fontWeight: 600 }} />
                  )}
                  <Chip label={`${components.length} added`} size="small"
                    sx={{ height: 20, fontSize: 11, bgcolor: alpha(GREEN, 0.1), color: GREEN_D, fontWeight: 700 }} />
                </Stack>
              </Box>

              {errors.components && (
                <Box sx={{ px: 2.5, py: 1, bgcolor: '#fff7ed', borderBottom: '1.5px solid #fed7aa' }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <WarningAmberIcon sx={{ fontSize: 15, color: '#d97706' }} />
                    <Typography fontSize={12} color="#d97706" fontWeight={600}>{errors.components}</Typography>
                  </Stack>
                </Box>
              )}

              {components.length === 0 ? (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                  <LayersIcon sx={{ fontSize: 40, color: '#CBD5E1', mb: 1 }} />
                  <Typography fontSize={14} fontWeight={600} color="text.secondary">No components added</Typography>
                  <Typography fontSize={12} color="text.secondary">Click elements in the left panel to add them</Typography>
                </Box>
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                  <DndContext
                    sensors={dndSensors}
                    collisionDetection={closestCenter}
                    onDragStart={({ active }) => setActiveDragId(active.id)}
                    onDragEnd={handleComponentDragEnd}
                    onDragCancel={() => setActiveDragId(null)}
                  >
                  <Table size="small" sx={{ minWidth: 600 }}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                        {['','#','Component','Calculation','Value / Formula','Monthly','Annual',''].map(h => (
                          <TableCell key={h || 'drag'} sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary',
                            textTransform: 'uppercase', letterSpacing: 0.5, py: 1.2,
                            borderColor: '#E8EDF5', whiteSpace: 'nowrap',
                            width: h === '' ? 36 : undefined, px: h === '' ? 0.5 : undefined }}>
                            {h}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <SortableContext items={componentSortIds} strategy={verticalListSortingStrategy}>
                    <TableBody>
                      {enriched.map((c, i) => {
                        const isFormula  = c.calculation_type === 'formula';
                        const isExpanded = expandedRows.has(c.element_id);
                        const vars       = detectVars(c.formula_expression);

                        return (
                          <SortableRowShell key={c.element_id} id={c.element_id}>
                            {({ setNodeRef, setActivatorNodeRef, style, attributes, listeners, isDragging }) => (
                          <React.Fragment>
                            {/* ── Main row ── */}
                            <TableRow
                              ref={setNodeRef}
                              style={style}
                              sx={{
                                '&:hover': { bgcolor: '#FAFBFC' },
                                ...(isFormula ? { bgcolor: '#fafbff' } : {}),
                                ...(isDragging ? {
                                  bgcolor: '#e0f7fa !important',
                                  boxShadow: '0 6px 20px rgba(8, 145, 178, 0.22)',
                                  outline: '2px solid #0891b2',
                                  outlineOffset: -2,
                                  opacity: 0.92,
                                } : {}),
                              }}
                            >
                              <TableCell sx={{ py: 0.5, px: 0.5, borderColor: '#F1F5F9', width: 36 }}>
                                <IconButton
                                  size="small"
                                  ref={setActivatorNodeRef}
                                  {...attributes}
                                  {...listeners}
                                  sx={{
                                    cursor: isDragging ? 'grabbing' : 'grab',
                                    color: '#94A3B8',
                                    '&:hover': { color: TEAL, bgcolor: '#f0fdfa' },
                                  }}
                                  aria-label={`Reorder ${c.element_name}`}
                                >
                                  <DragIndicatorIcon sx={{ fontSize: 20 }} />
                                </IconButton>
                              </TableCell>
                              <TableCell sx={{ py: 1, borderColor: '#F1F5F9', color: '#9CA3AF', fontSize: 12, fontWeight: 600 }}>
                                {i + 1}
                              </TableCell>

                              <TableCell sx={{ py: 1, borderColor: '#F1F5F9' }}>
                                <Typography fontSize={13} fontWeight={600} color="#374151">{c.element_name}</Typography>
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                  <Typography fontSize={10.5} color="text.secondary" fontFamily="monospace">{c.element_code}</Typography>
                                  {isFormula && (
                                    <Chip label={c.is_formula_override ? 'Custom Formula' : 'Default Formula'}
                                      size="small"
                                      sx={{ height: 14, fontSize: 9, px: 0.3,
                                        bgcolor: c.is_formula_override ? '#fff7ed' : '#eff6ff',
                                        color:   c.is_formula_override ? AMBER    : BLUE,
                                        fontWeight: 700 }} />
                                  )}
                                </Stack>
                              </TableCell>

                              {/* Calculation type cell */}
                              <TableCell sx={{ py: 1, borderColor: '#F1F5F9' }}>
                                {isFormula ? (
                                  <Chip icon={<FunctionsIcon sx={{ fontSize: 13 }} />} label="Formula"
                                    size="small"
                                    sx={{ bgcolor: '#eff6ff', color: BLUE, fontWeight: 700, fontSize: 12, height: 26 }} />
                                ) : (
                                  <Select size="small" value={c.calculation_type}
                                    onChange={e => setComp(i, 'calculation_type', e.target.value)}
                                    sx={{ fontSize: 12.5, borderRadius: '8px', minWidth: 155,
                                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: GREEN } }}>
                                    <MenuItem value="flat">
                                      <Stack direction="row" alignItems="center" spacing={0.75}>
                                        <CurrencyRupeeIcon sx={{ fontSize: 14, color: GREEN }} />
                                        <span>Flat (₹/month)</span>
                                      </Stack>
                                    </MenuItem>
                                    <MenuItem value="percentage_of_ctc">
                                      <Stack direction="row" alignItems="center" spacing={0.75}>
                                        <PercentIcon sx={{ fontSize: 14, color: TEAL }} />
                                        <span>% of CTC</span>
                                      </Stack>
                                    </MenuItem>
                                    <MenuItem value="percentage_of_basic">
                                      <Stack direction="row" alignItems="center" spacing={0.75}>
                                        <PercentIcon sx={{ fontSize: 14, color: '#7c3aed' }} />
                                        <span>% of Basic</span>
                                      </Stack>
                                    </MenuItem>
                                  </Select>
                                )}
                              </TableCell>

                              {/* Value / Formula cell */}
                              <TableCell sx={{ py: 1, borderColor: '#F1F5F9' }}>
                                {isFormula ? (
                                  <Tooltip title={c.formula_expression || 'No formula set'}>
                                    <Box
                                      onClick={() => toggleExpand(c.element_id)}
                                      sx={{
                                        display: 'inline-flex', alignItems: 'center', gap: 0.5,
                                        px: 1.5, py: 0.6, borderRadius: '8px',
                                        border: `1.5px dashed ${isExpanded ? BLUE : '#CBD5E1'}`,
                                        bgcolor: isExpanded ? '#eff6ff' : '#F8FAFC',
                                        cursor: 'pointer',
                                        transition: 'all 0.15s',
                                        '&:hover': { borderColor: BLUE, bgcolor: '#eff6ff' },
                                      }}>
                                      <FunctionsIcon sx={{ fontSize: 13, color: isExpanded ? BLUE : '#9CA3AF' }} />
                                      <Typography fontSize={11.5} fontFamily="monospace" color={isExpanded ? BLUE : '#6B7280'}
                                        sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {c.formula_expression || 'Set formula…'}
                                      </Typography>
                                      {isExpanded
                                        ? <ExpandLessIcon sx={{ fontSize: 13, color: BLUE }} />
                                        : <ExpandMoreIcon sx={{ fontSize: 13, color: '#9CA3AF' }} />}
                                    </Box>
                                  </Tooltip>
                                ) : c.calculation_type === 'flat' ? (
                                  <TextField size="small" type="number"
                                    value={c.value}
                                    onChange={e => setComp(i, 'value', e.target.value)}
                                    inputProps={{ min: 0, step: 1 }}
                                    InputProps={{
                                      startAdornment: <InputAdornment position="start"><Typography fontSize={12} color={GREEN} fontWeight={700}>₹</Typography></InputAdornment>,
                                      sx: { fontFamily: 'monospace', fontSize: 13 },
                                    }}
                                    sx={{ ...inputSx, width: 120 }}
                                  />
                                ) : (
                                  /* percentage_of_ctc or percentage_of_basic */
                                  <Stack direction="row" alignItems="center" spacing={0.75}>
                                    <TextField size="small" type="number"
                                      value={c.value}
                                      onChange={e => setComp(i, 'value', e.target.value)}
                                      inputProps={{ min: 0, max: 100, step: 0.1 }}
                                      InputProps={{
                                        endAdornment: <InputAdornment position="end"><Typography fontSize={11} color="text.secondary">%</Typography></InputAdornment>,
                                        sx: { fontFamily: 'monospace', fontSize: 13 },
                                      }}
                                      sx={{ ...inputSx, width: 90 }}
                                    />
                                    <Chip
                                      label={c.calculation_type === 'percentage_of_basic' ? 'of Basic' : 'of CTC'}
                                      size="small"
                                      sx={{
                                        height: 20, fontSize: 10.5, fontWeight: 700,
                                        bgcolor: c.calculation_type === 'percentage_of_basic' ? '#f5f3ff' : '#f0f9ff',
                                        color:   c.calculation_type === 'percentage_of_basic' ? '#7c3aed'  : TEAL,
                                        border: `1px solid ${c.calculation_type === 'percentage_of_basic' ? '#ddd6fe' : '#bae6fd'}`,
                                      }}
                                    />
                                  </Stack>
                                )}
                              </TableCell>

                              {/* Monthly */}
                              <TableCell sx={{ py: 1, borderColor: '#F1F5F9' }}>
                                {isFormula ? (
                                  c.monthly_amount !== null
                                    ? <Typography fontSize={13} fontWeight={700} color="#374151" fontFamily="monospace">₹{fmt(c.monthly_amount)}</Typography>
                                    : <Chip label="Runtime" size="small" sx={{ height: 18, fontSize: 10, bgcolor: '#F1F5F9', color: '#6B7280' }} />
                                ) : c.monthly_amount !== null && c.value !== '' ? (
                                  <Typography fontSize={13} fontWeight={700} color="#374151" fontFamily="monospace">₹{fmt(c.monthly_amount)}</Typography>
                                ) : (
                                  <Typography fontSize={13} color="#CBD5E1">—</Typography>
                                )}
                              </TableCell>

                              {/* Annual */}
                              <TableCell sx={{ py: 1, borderColor: '#F1F5F9' }}>
                                {isFormula ? (
                                  c.annual_amount !== null
                                    ? <Typography fontSize={13} fontWeight={700} color="#374151" fontFamily="monospace">₹{fmt(c.annual_amount)}</Typography>
                                    : <Chip label="Runtime" size="small" sx={{ height: 18, fontSize: 10, bgcolor: '#F1F5F9', color: '#6B7280' }} />
                                ) : c.annual_amount !== null && c.value !== '' ? (
                                  <Typography fontSize={13} fontWeight={700} color="#374151" fontFamily="monospace">₹{fmt(c.annual_amount)}</Typography>
                                ) : (
                                  <Typography fontSize={13} color="#CBD5E1">—</Typography>
                                )}
                              </TableCell>

                              {/* Actions */}
                              <TableCell sx={{ py: 1, borderColor: '#F1F5F9' }}>
                                <Stack direction="row" spacing={0.5}>
                                  {isFormula && (
                                    <Tooltip title={isExpanded ? 'Collapse formula editor' : 'Edit formula'}>
                                      <IconButton size="small" onClick={() => toggleExpand(c.element_id)}
                                        sx={{ color: BLUE, '&:hover': { bgcolor: '#eff6ff' } }}>
                                        <FunctionsIcon sx={{ fontSize: 16 }} />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  <IconButton size="small" onClick={() => removeComponent(i)}
                                    sx={{ color: '#EF4444', '&:hover': { bgcolor: '#fef2f2' } }}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Stack>
                              </TableCell>
                            </TableRow>

                            {/* ── Formula editor row (expandable) ── */}
                            {isFormula && (
                              <TableRow sx={isDragging ? { bgcolor: '#f0fdfa' } : undefined}>
                                <TableCell colSpan={8} sx={{ p: 0, borderBottom: isExpanded ? '1px solid #E8EDF5' : 'none', borderColor: '#F1F5F9' }}>
                                  <Collapse in={isExpanded} unmountOnExit>
                                      {(() => {
                                        const draftValue = formulaDrafts[c.element_id] !== undefined ? formulaDrafts[c.element_id] : (c.formula_expression || '');
                                        const isModified = formulaDrafts[c.element_id] !== undefined && formulaDrafts[c.element_id] !== (c.formula_expression || '');

                                        return (
                                          <Box sx={{ mx: 2, my: 1.5, p: 2, bgcolor: '#f8faff',
                                            borderRadius: '10px', border: `1.5px solid ${c.is_formula_override ? '#fed7aa' : '#bfdbfe'}` }}>

                                            {/* Badge + toggle */}
                                            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5} flexWrap="wrap" gap={1}>
                                              <Stack direction="row" alignItems="center" spacing={1}>
                                                <FunctionsIcon sx={{ fontSize: 16, color: BLUE }} />
                                                <Typography fontSize={12} fontWeight={700} color="#374151">Formula Expression</Typography>
                                                {c.is_formula_override
                                                  ? <Chip label="Custom Formula Applied" size="small"
                                                      sx={{ height: 20, fontSize: 10.5, bgcolor: '#fff7ed', color: AMBER, fontWeight: 700 }} />
                                                  : <Chip label="Default Formula Loaded" size="small"
                                                      sx={{ height: 20, fontSize: 10.5, bgcolor: '#eff6ff', color: BLUE, fontWeight: 700 }} />
                                                }
                                              </Stack>
                                              <Stack direction="row" spacing={1}>
                                                {c.is_formula_override && c.default_formula_expression && (
                                                  <Button size="small" variant="outlined"
                                                    onClick={() => {
                                                      setComp(i, 'formula_expression', c.default_formula_expression);
                                                      setComp(i, 'is_formula_override', false);
                                                      setFormulaDrafts(prev => { const n = {...prev}; delete n[c.element_id]; return n; });
                                                    }}
                                                    sx={{ fontSize: 11, py: 0.4, px: 1.5, borderRadius: '8px', borderColor: '#bfdbfe', color: BLUE,
                                                      '&:hover': { borderColor: BLUE, bgcolor: '#eff6ff' } }}>
                                                    Revert to Default
                                                  </Button>
                                                )}
                                                {!c.is_formula_override && (
                                                  <Button size="small" variant="outlined"
                                                    onClick={() => setComp(i, 'is_formula_override', true)}
                                                    sx={{ fontSize: 11, py: 0.4, px: 1.5, borderRadius: '8px', borderColor: '#fed7aa', color: AMBER,
                                                      '&:hover': { borderColor: AMBER, bgcolor: '#fff7ed' } }}>
                                                    Override Formula
                                                  </Button>
                                                )}
                                                {(c.is_formula_override || !c.default_formula_expression) && (
                                                  <Button size="small" variant="contained"
                                                    disabled={!isModified}
                                                    onClick={() => handleUpdateFormulaClick(c, i, draftValue)}
                                                    sx={{ fontSize: 11, py: 0.4, px: 1.5, borderRadius: '8px', 
                                                      bgcolor: isModified ? BLUE : '#e2e8f0', color: isModified ? 'white' : '#94a3b8',
                                                      '&:hover': { bgcolor: isModified ? '#1e3a8a' : '#e2e8f0' } }}>
                                                    Update
                                                  </Button>
                                                )}
                                              </Stack>
                                            </Stack>

                                            {/* Formula text field */}
                                            {c.default_formula_expression && !c.is_formula_override && (
                                              <Box sx={{ mb: 1.5, px: 1.5, py: 1, bgcolor: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
                                                <Typography fontSize={10.5} color={BLUE} fontWeight={600} mb={0.3}>Default formula (read-only):</Typography>
                                                <Typography fontSize={12.5} fontFamily="monospace" color="#1e3a5f">
                                                  {c.default_formula_expression}
                                                </Typography>
                                              </Box>
                                            )}

                                            {(c.is_formula_override || !c.default_formula_expression) && (
                                              <TextField
                                                fullWidth size="small"
                                                label="Formula expression"
                                                value={draftValue}
                                                onChange={e => {
                                                  setFormulaDrafts(prev => ({ ...prev, [c.element_id]: e.target.value }));
                                                }}
                                                placeholder="e.g. basic * 0.12  or  hours * rate * 1.5"
                                                multiline
                                                minRows={2}
                                                InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
                                                sx={{ ...inputSx, mb: 1.5 }}
                                              />
                                            )}

                                            {/* Variable chips */}
                                            {vars.length > 0 && (
                                              <Box>
                                                <Typography fontSize={10.5} color="text.secondary" fontWeight={600} mb={0.5}>
                                                  Variables detected ({vars.length}):
                                                </Typography>
                                                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                                                  {vars.map(v => (
                                                    <Chip key={v} label={v} size="small"
                                                      sx={{ fontFamily: 'monospace', fontSize: 11, height: 20,
                                                        bgcolor: '#f0f9ff', color: '#0369a1', fontWeight: 600,
                                                        border: '1px solid #bae6fd' }} />
                                                  ))}
                                                </Stack>
                                                <Typography fontSize={10.5} color="text.secondary" mt={0.75}>
                                                  These variables must be provided at payroll run time.
                                                </Typography>
                                              </Box>
                                            )}
                                          </Box>
                                        );
                                      })()}
                                    </Collapse>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                            )}
                          </SortableRowShell>
                        );
                      })}
                    </TableBody>
                    </SortableContext>
                  </Table>
                  <DragOverlay dropAnimation={{ duration: 220, easing: 'cubic-bezier(0.18, 0.67, 0.6, 1)' }}>
                    {draggedComponent ? (
                      <Box sx={{
                        px: 2, py: 1.25, minWidth: 280,
                        bgcolor: '#e0f7fa',
                        border: '2px solid #0891b2',
                        borderRadius: '10px',
                        boxShadow: '0 10px 28px rgba(8, 145, 178, 0.28)',
                      }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <DragIndicatorIcon sx={{ fontSize: 20, color: TEAL }} />
                          <Box>
                            <Typography fontSize={13} fontWeight={700} color="#374151">
                              {draggedComponent.element_name}
                            </Typography>
                            <Typography fontSize={10.5} color="text.secondary" fontFamily="monospace">
                              {draggedComponent.element_code}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    ) : null}
                  </DragOverlay>
                  </DndContext>
                </Box>
              )}

              {/* Totals footer */}
              {components.length > 0 && (
                <Box sx={{ px: 2.5, py: 2, borderTop: '2px solid #E8EDF5', bgcolor: '#F8FAFC' }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1.5}>
                    <Stack direction="row" spacing={3}>
                      <Box>
                        <Typography fontSize={11} color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.4}>Total Monthly</Typography>
                        <Typography fontSize={18} fontWeight={800} color="#111827" fontFamily="monospace">₹{fmt(totalMonthly)}</Typography>
                      </Box>
                      <Divider orientation="vertical" flexItem sx={{ borderColor: '#E8EDF5' }} />
                      <Box>
                        <Typography fontSize={11} color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.4}>Total Annual</Typography>
                        <Typography fontSize={18} fontWeight={800} color="#111827" fontFamily="monospace">₹{fmt(totalAnnual)}</Typography>
                      </Box>
                    </Stack>
                    {annualCtcNum > 0 && (
                      <Box sx={{
                        px: 2, py: 1, borderRadius: '10px',
                        bgcolor: Math.abs(difference) < 1 ? '#f0fdf4' : difference > 0 ? '#fff7ed' : '#fef2f2',
                        border: `1px solid ${Math.abs(difference) < 1 ? '#bbf7d0' : difference > 0 ? '#fed7aa' : '#fecaca'}`,
                      }}>
                        <Typography fontSize={11} color="text.secondary" fontWeight={600}>Monthly CTC: ₹{fmt(monthlyCtcNum)}</Typography>
                        <Typography fontSize={13} fontWeight={700}
                          color={Math.abs(difference) < 1 ? '#16a34a' : difference > 0 ? '#d97706' : '#dc2626'}>
                          {Math.abs(difference) < 1
                            ? '✓ Balanced'
                            : difference > 0
                            ? `₹${fmt(difference)} remaining/mo`
                            : `₹${fmt(-difference)} over CTC/mo`}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
      {confirmFormulaState.open && (
        <ConfirmDialog
          open={confirmFormulaState.open}
          title="Update Formula"
          message="Are you sure you want to update this formula? The changes will be used in future payroll calculations."
          confirmLabel="Update"
          cancelLabel="Cancel"
          confirmColor="primary"
          onConfirm={confirmUpdateFormula}
          onCancel={() => setConfirmFormulaState({ open: false, index: -1, c: null, draftValue: '' })}
        />
      )}
    </Box>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN PAGE — manages list ↔ editor navigation
// ═══════════════════════════════════════════════════════════════════════════════
export default function SalaryTemplatePage() {
  const { enqueueSnackbar } = useSnackbar();
  const { state } = useApp();
  const activeContextKey = [
    state.activeContext?.company_id || '',
    state.activeContext?.bg_id || '',
    state.activeContext?.ldg_id || '',
    state.activeContext?.payroll_id || '',
  ].join('|');
  const [view,        setView]       = useState('list');
  const [editingId,   setEditingId]  = useState(null);
  const [templates,   setTemplates]  = useState([]);
  const [listLoading, setListLoading] = useState(false);

  const [toggleTarget, setToggleTarget] = useState(null);
  const [confirmToggleOpen, setConfirmToggleOpen] = useState(false);
  const [assignedCount, setAssignedCount] = useState(0);
  const [toggleLoading, setToggleLoading] = useState(false);

  const loadTemplates = useCallback(() => {
    setListLoading(true);
    fetchSalaryTemplates()
      .then(r => setTemplates(r.data?.data || []))
      .catch(() => enqueueSnackbar('Failed to load templates', { variant: 'error' }))
      .finally(() => setListLoading(false));
  }, []);

  useEffect(() => { loadTemplates(); }, [loadTemplates, activeContextKey]);

  const handleAdd  = () => { setEditingId(null); setView('editor'); };
  const handleEdit = (t) => { setEditingId(t.template_id); setView('editor'); };
  const handleBack = () => { setView('list'); setEditingId(null); };

  const handleSaved = () => {
    setView('list');
    setEditingId(null);
    loadTemplates();
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Delete "${t.template_name}"? This cannot be undone.`)) return;
    try {
      await deleteSalaryTemplate(t.template_id);
      enqueueSnackbar('Template deleted', { variant: 'success' });
      loadTemplates();
    } catch {
      enqueueSnackbar('Delete failed', { variant: 'error' });
    }
  };

  const handleBulkDelete = async (ids) => {
    try {
      await Promise.all(ids.map(id => deleteSalaryTemplate(id)));
      enqueueSnackbar(`Successfully deleted ${ids.length} templates`, { variant: 'success' });
      loadTemplates();
      return true;
    } catch {
      enqueueSnackbar('Bulk delete failed', { variant: 'error' });
      return false;
    }
  };

  const handleToggleStatus = async (template, checked) => {
    const newStatus = checked ? 'ACTIVE' : 'INACTIVE';
    setToggleTarget({ ...template, _targetStatus: newStatus });

    if (newStatus === 'INACTIVE') {
      setToggleLoading(true);
      try {
        const [salRes, asnRes] = await Promise.all([
          fetchRecords('SALARY'),
          fetchRecords('ASSIGNMENT')
        ]);
        const sCount = (salRes.data?.data || []).filter(s => s.template_id === template.template_id).length;
        const aCount = (asnRes.data?.data || []).filter(a => a.default_template_id === template.template_id).length;
        setAssignedCount(sCount + aCount);
        setConfirmToggleOpen(true);
      } catch (e) {
        enqueueSnackbar('Error checking assignments', { variant: 'error' });
      } finally {
        setToggleLoading(false);
      }
    } else {
      setConfirmToggleOpen(true);
    }
  };

  const confirmToggle = async () => {
    if (!toggleTarget) return;
    const isActivating = toggleTarget._targetStatus === 'ACTIVE';
    try {
      await toggleSalaryTemplateStatus(toggleTarget.template_id, toggleTarget._targetStatus);
      enqueueSnackbar(isActivating ? 'Template activated' : 'Template inactivated', { variant: 'success' });
      loadTemplates();
    } catch (e) {
      enqueueSnackbar(isActivating ? 'Error activating template' : 'Error inactivating template', { variant: 'error' });
    } finally {
      setConfirmToggleOpen(false);
      setToggleTarget(null);
    }
  };

  if (view === 'editor') {
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <TemplateEditor templateId={editingId} onBack={handleBack} onSaved={handleSaved} />
      </Box>
    );
  }

  return (
    <>
      <TemplateList
        templates={templates}
        loading={listLoading || toggleLoading}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onToggleStatus={handleToggleStatus}
        onBulkDelete={handleBulkDelete}
      />
      
      {toggleTarget && (
        <ConfirmDialog
          open={confirmToggleOpen}
          title={toggleTarget._targetStatus === 'ACTIVE' ? "Activate Template?" : "Inactivate Template?"}
          message={
            toggleTarget._targetStatus === 'ACTIVE'
              ? "This template will be available in dropdowns and can be assigned to employees."
              : "This template will be hidden from dropdowns for new assignments. Existing employee assignments and payroll history will remain unchanged."
          }
          confirmLabel={toggleTarget._targetStatus === 'ACTIVE' ? "Yes, Activate" : "Yes, Inactivate"}
          confirmColor={toggleTarget._targetStatus === 'ACTIVE' ? "success" : "warning"}
          confirmIcon={toggleTarget._targetStatus === 'ACTIVE' ? "success" : "none"}
          onConfirm={confirmToggle}
          onCancel={() => { setConfirmToggleOpen(false); setToggleTarget(null); }}
        />
      )}
    </>
  );
}
